/* eslint-disable no-underscore-dangle */
import Access from './Access.js'
import Shard from './Shard.js'

/**
 * @class Returns a new FirebaseStore class that will use the firebase library passed in. This should be either 'firebase' (on browser) or 'firebase-admin' on server.
 * @param {object} options
 * @param {object} firebase firebase lib
 **/
export default class BaseFirebaseStore {

  constructor( options, firebase, shardKey ) {

    this._options = options
    this._firebase = firebase
    this._shardCache = options.shardCache || [ ]
    this._shardKey = shardKey
    this._shardId = this.shardId( shardKey )

    if ( options.firebaseApp && options.rootRef ) {

      // derived from another non-sharded store
      this._app = options.firebaseApp
      this._rootRef = options.rootRef

    } else {

      // new store, create the connection
      let fbOpts
      let name
      let app = null

      // check for config (web usage vs. databaseURL is server)
      if ( options.config ) {
        // This one doesnt need sharding. the source should already set the root
        // and the url correct to the shard
        fbOpts = options.config
        name = `${options.config.databaseURL}/${options.root}`
      } else if ( options.databaseURL ) {
        if ( this._shardId >= 0 && this._shardCache[this._shardId] ) {
          app = this._shardCache[this._shardId].app
          fbOpts = this._shardCache[this._shardId].opts
        } else {
          fbOpts = {
            databaseURL : this.getDatabaseURL(),
            credential : options.credential || firebase.credential.applicationDefault(),
            serviceAccountId : options.serviceAccountId || undefined,
            storageBucket : options.storageBucket || null,
          }
        }
        name = fbOpts.databaseURL
      } else {
        fbOpts = {}
      }

      // find the app with the same root name
      // const app = appByName( firebase, name )

      // this will fail on the second call without a name
      this._app = app || appByName( firebase, name ) || firebase.initializeApp( fbOpts, name )
      if ( this._shardId >= 0 && this._shardCache[this._shardId] == null ) {
        this._shardCache[this._shardId] = { app : this._app, opts : fbOpts }
      }
      // get root ref
      const db = this._app.database()
      this._rootRef = options.root && options.root.length > 0 ? db.ref( options.root ) : db.ref()
      this._jwt = options.jwt
    }
  }

  getDatabaseURL( ) {
    const opts = this._options
    return Shard.getUrl( this._shardId, opts.shards && opts.shards.databaseURL, opts.databaseURL )
  }

  /**
   * @function signIn If on the client side, sign in with a custom jwt token
   * @param {string} jwt json web token to connect to firebase
   **/
  signIn( jwt ) {
    if ( jwt || this._jwt ) {
      return this._app.auth().signInWithCustomToken( jwt || this._jwt )
    }
    const r = this
    return new Promise( ( ( resolve, reject ) => { resolve( r ) } ) )

  }

  /**
   * @function child Create a child at the specified relative path, using the specified class
   * @param {string} path data path
   * @param {class} ClassType instance of this class will be returned
   **/
  child( path, ClassType, shardKey = null ) {
    if ( shardKey && hasShardConfig( this._options ) ) {
      return new ClassType( {
        ...this._options,
        shardCache : this._shardCache,
        firebaseApp : null,
        rootRef : null,
        root : this._rootRef.child( path ).toString().split( '/' ).slice( 3 )
          .join( '/' )
      }, this._firebase, shardKey )
    }
    return new ClassType( {
      ...this._options,
      firebaseApp : this._app,
      rootRef : this._rootRef.child( path )
    }, this._firebase )
  }

  /**
   * @function access Creates an Access object referencing the location retrive to the current root
   * @param {string} path data path
   **/
  access( path ) {
    path = ( path && path.trim() ) || ''
    return new Access( path.length === 0 ? this._rootRef : this._rootRef.child( path ), this._firebase )
  }

  /**
   * @function service If invoked with no arguments, this will return a Access object pointing at root/srv (which is not normal except for perhaps monitoring). Otherwise, an Access object pointing to <root>/srv/<name> is created
   * @param {string} name
   **/
  service( name ) {
    if ( arguments.length === 0 ) {
      return new Access( this._rootRef.child( 'srv' ), this._firebase )
    }
    name = name == null ? null : name.trim()
    if ( name == null || name.length === 0 ) {
      console.log( 'invalid name' ) /* eslint-disable-line no-console */
      throw new Error( 'service must be invoked with a non-null name' )
    }
    return new Access( this._rootRef.child( `srv/${name}` ), this._firebase )
  }

  /**
   * @function databaseRoot Returns an Access object to the root. Not normally used, as it exists the root bountry of this object. Be very careful using this.
   **/
  databaseRoot( ) {
    return new Access( this._app.database().ref(), this._firebase )
  }

  /**
   * @function timestampValue returns the timestamp of the server
   */
  timestampValue() {
    return this._firebase.database.ServerValue.TIMESTAMP
  }

  shardId( key ) {
    if ( key == null || !hasShardConfig( this._options ) ) {
      return -1
    }
    return Shard.shardId( this._options.shards.number, key )
  }
}

function hasShardConfig( options ) {
  if ( options.shards ) {
    const url = options.shards.databaseURL
    const num = options.shards.number
    return typeof num === 'number' && num > 1 && typeof url === 'string'
  }
  return false
}

/**
 * @function appByName searchs firebase instance for an app with the name
 * @param {object} firebase
 * @param {string} name name of firebase app to search for
 */
function appByName( firebase, name ) {
  for ( let n = 0; n < firebase.apps.length; n++ ) {
    if ( firebase.apps[n].name === name ) {
      return firebase.apps[n]
    }
  }
  return null
}
